import React, { useEffect, useState } from "react";
import { getAdminReservations, fetchReservationData } from "../../api/reservationApi";
import { getUserAccountByAdmin } from '../../api/userApi';

interface Reservation {
  id: number;
  space: { title: string; location: string };
  depositor: { id: number; email: string };
  start_date: string;
  end_date: string;
  status: string;
  total_price: number;
  payments: any[];
  order_id: string;
}

const STATUS_LIST = [
    "전체",
    "PENDING",
    "APPROVED",
    "REJECTED",
    "PAYMENT_COMPLETED",
    "STORED",
    "RETURNED",
    "FINISHED",
    "CANCELED",
    "DELETED",
];

export default function AdminReservationList() {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedReservations, setSelectedReservations] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<string>("status");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>("전체");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  // 추가: 계좌 확인 모달 상태 관리
  const [accountInfo, setAccountInfo] = useState<any | null>(null);
  const [accountModalOpen, setAccountModalOpen] = useState(false);

  // 상태 추가: 계좌 정보, 로딩 중 row 관리
  const [accountRowMap, setAccountRowMap] = useState<{[reservationId: number]: any | null}>({});
  const [loadingRow, setLoadingRow] = useState<number | null>(null);

  const fetchReservationsData = async () => {
    try {
      const { data, total } = await getAdminReservations(page, limit);
      setReservations(data);
      setTotal(total);
    } catch (err) {
      setError("Failed to fetch reservations.");
    }
  };

  useEffect(() => {
    fetchReservationsData();
  }, [page]);

  const filteredReservations =
    statusFilter === "전체"
      ? reservations
      : reservations.filter((res) => res.status === statusFilter);

  const handleSort = (key: keyof Reservation) => {
    const order = sortBy === key && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(key);
    setSortOrder(order);
    const sorted = [...reservations].sort((a, b) => {
      if (order === "asc") return a[key] > b[key] ? 1 : -1;
      return a[key] < b[key] ? 1 : -1;
    });
    setReservations(sorted);
  };

  const handleCheckboxChange = (id: number) => {
    setSelectedReservations((prev) =>
      prev.includes(id) ? prev.filter((resId) => resId !== id) : [...prev, id]
    );
  };

  // 계좌 정보 직접 표시용 핸들러 (reservationId, userId 필요)
  const handleAccountCheck = async (reservationId: number, userId: number) => {
    setLoadingRow(reservationId);
    try {
      const info = await getUserAccountByAdmin(userId);
      setAccountRowMap(prev => ({ ...prev, [reservationId]: info }));
    } catch (err) {
      alert('계좌 정보 조회에 실패했습니다.');
    } finally {
      setLoadingRow(null);
    }
  };
  const handleAccountHide = (reservationId: number) => {
    setAccountRowMap(prev => {
      const clone = { ...prev };
      delete clone[reservationId];
      return clone;
    });
  };

  const handleConfirm = async () => {
    const selected = reservations.filter(
      (res) => selectedReservations.includes(res.id) && res.status === "RETURNED"
    );
    if (selected.length === 0) {
      alert("No valid reservations selected for status update.");
      return;
    }

    if (!window.confirm(`${selected.length}건을 완료 처리하시겠습니까?`)) return;

    try {
      for (const reservation of selected) {
        await fetchReservationData(reservation.id, { status: "FINISHED" });
      }
      fetchReservationsData();
      setSelectedReservations([]);
      alert("처리 완료");
    } catch (err) {
      setError("예약 상태 변경 실패");
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">물건 예약 관리</h1>

      {error && <div className="text-red-500 mb-4">{error}</div>}

      {/* Status Filter Dropdown */}
      <div className="mb-4 relative inline-block text-left">
        <div>
          <button
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="inline-flex justify-between w-48 rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            status: {statusFilter}
            <svg
              className="-mr-1 ml-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {dropdownOpen && (
          <div
            className="origin-top-left absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
          >
            <div className="py-1">
              {STATUS_LIST.map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setStatusFilter(status);
                    setDropdownOpen(false);
                  }}
                  className={`block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 ${
                    statusFilter === status ? "bg-gray-100 font-semibold" : ""
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <table className="min-w-full border border-gray-200 text-sm">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-2 border">ID</th>
            <th className="p-2 border cursor-pointer" onClick={() => handleSort("space")}>Space</th>
            <th className="p-2 border cursor-pointer" onClick={() => handleSort("depositor")}>Depositor</th>
            <th className="p-2 border cursor-pointer" onClick={() => handleSort("start_date")}>Start Date</th>
            <th className="p-2 border cursor-pointer" onClick={() => handleSort("end_date")}>End Date</th>
            <th className="p-2 border cursor-pointer" onClick={() => handleSort("status")}>Status</th>
            <th className="p-2 border">Price</th>
            <th className="p-2 border">Payment</th>
            <th className="p-2 border">Order ID</th>
            <th className="p-2 border">Select</th>
            <th className="p-2 border">계좌</th>
          </tr>
        </thead>
        <tbody>
          {filteredReservations.map((res) => (
            <tr key={res.id} className="hover:bg-gray-50">
              <td className="p-2 border">{res.id}</td>
              <td className="p-2 border">{res.space?.title}</td>
              <td className="p-2 border">{res.depositor?.email}</td>
              <td className="p-2 border">{res.start_date}</td>
              <td className="p-2 border">{res.end_date}</td>
              <td className="p-2 border">{res.status}</td>
              <td className="p-2 border">{res.total_price?.toLocaleString()}원</td>
              <td className="p-2 border">{res.payments?.length > 0 ? "Yes" : "No"}</td>
              <td className="p-2 border">{res.order_id}</td>
              <td className="p-2 border text-center">
                <input
                  type="checkbox"
                  className={`w-4 h-4 rounded-sm transition duration-200 ${
                    selectedReservations.includes(res.id) ? "border-green-500 ring-1 ring-green-500" : "border-gray-300"
                  }`}
                  disabled={res.status !== "RETURNED"}
                  checked={selectedReservations.includes(res.id)}
                  onChange={() => handleCheckboxChange(res.id)}
                />
              </td>
              <td className="p-2 border text-center">
                {accountRowMap[res.id] ? (
                  <div>
                    <div>
                      <b>은행:</b> {accountRowMap[res.id]?.bank_name ?? '-'} <br />
                      <b>계좌:</b> {accountRowMap[res.id]?.account_number_decrypted ?? '계좌정보 없음'} <br />
                      <b>예금주:</b> {accountRowMap[res.id]?.account_holder_name ?? '-'}
                      <button onClick={() => handleAccountHide(res.id)} className="ml-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">숨기기</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleAccountCheck(res.id, res.depositor?.id)}
                    className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded"
                    disabled={loadingRow === res.id}
                  >
                    {loadingRow === res.id ? '로딩...' : '계좌 보기'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-center gap-4">
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page === 1}
          className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50"
        >
          이전
        </button>
        <span className="text-sm">페이지 {page} / {Math.ceil(total / limit)}</span>
        <button
          onClick={() => setPage((p) => (p * limit < total ? p + 1 : p))}
          disabled={page * limit >= total}
          className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50"
        >
          다음
        </button>
      </div>

      {/* Confirm Button */}
      <div className="mt-6 text-center">
        <button
          onClick={handleConfirm}
          disabled={selectedReservations.length === 0}
          className="px-6 py-2 border border-green-500 text-black-600 rounded hover:bg-green-100 disabled:opacity-40"
        >
          확인
        </button>
      </div>
    </div>
  );
}
